/* eslint-disable react/no-array-index-key */
/* eslint-disable react/destructuring-assignment -- discriminated union
   narrowing on `props.entityType` doesn't survive destructuring. */
import { FC, ReactElement } from 'react';
import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { TAllowedOperationsValueUI } from 'data/types/generic.types';
import { TPeopleByIdUI } from 'data/types/people.types';
import { TOrganizationByIdUI } from 'data/types/organizations.types';
import { EDuplicateEntityType } from 'data/types/duplicateManagement.types';
import {
  SCompareCard,
  SCompareWrapper,
  SFieldRow,
} from '../../DuplicateMerge.styles';

// Production compare cards. Renders side-by-side field values, with
// green-check for matching values and orange-warning + strikethrough on the
// secondary card for diffs. Smart-merge rule per Confluence §3.8: primary
// keeps its value when both are set; secondary value is copied only when
// primary is empty.

type TBaseProps = {
  // "willMerge" → present tense (the row is in the review list).
  // "merged"    → past tense (the row is on the history tab — T7).
  mode: 'willMerge' | 'merged';
};

type TPeopleProps = TBaseProps & {
  entityType: EDuplicateEntityType.PEOPLE;
  primary: TPeopleByIdUI;
  secondary: TPeopleByIdUI;
};

type TOrgProps = TBaseProps & {
  entityType: EDuplicateEntityType.ORGANIZATION;
  primary: TOrganizationByIdUI;
  secondary: TOrganizationByIdUI;
};

type TCompareCardsProps = TPeopleProps | TOrgProps;

const v = <T,>(field: TAllowedOperationsValueUI<T> | undefined): T | '' => {
  if (!field) return '' as const;
  return (field.value ?? '') as T | '';
};

const renderRow = (
  label: string,
  primaryValue: unknown,
  secondaryValue: unknown,
  isPrimaryCard: boolean,
  rowKey: number,
) => {
  const diff = String(primaryValue ?? '') !== String(secondaryValue ?? '');
  const val = isPrimaryCard ? primaryValue : secondaryValue;
  const strike = !isPrimaryCard && diff;
  return (
    <SFieldRow key={rowKey} $diff={diff} $strike={strike}>
      <span className="field-label">{label}</span>
      <span className="field-value">{String(val ?? '—') || '—'}</span>
      <span className="field-indicator">
        {diff ? <WarningFilled /> : <CheckCircleFilled />}
      </span>
    </SFieldRow>
  );
};

const peopleRows = (
  primary: TPeopleByIdUI,
  secondary: TPeopleByIdUI,
  isPrimaryCard: boolean,
): ReactElement[] => {
  const fields: [string, unknown, unknown][] = [
    ['First name', v(primary.firstName), v(secondary.firstName)],
    ['Last name', v(primary.lastName), v(secondary.lastName)],
    ['Email', v(primary.email), v(secondary.email)],
    ['Phone', v(primary.phone), v(secondary.phone)],
    ['Title', v(primary.title), v(secondary.title)],
  ];
  return fields.map(([label, p, s], i) =>
    renderRow(label, p, s, isPrimaryCard, i),
  );
};

const orgRows = (
  primary: TOrganizationByIdUI,
  secondary: TOrganizationByIdUI,
  isPrimaryCard: boolean,
): ReactElement[] => {
  const fields: [string, unknown, unknown][] = [
    ['Name', v(primary.name), v(secondary.name)],
    [
      'Address',
      primary.address?.formattedAddress || '',
      secondary.address?.formattedAddress || '',
    ],
    ['Email', v(primary.email), v(secondary.email)],
    ['Phone', v(primary.phone), v(secondary.phone)],
  ];
  return fields.map(([label, p, s], i) =>
    renderRow(label, p, s, isPrimaryCard, i),
  );
};

// eslint-disable-next-line react/destructuring-assignment -- discriminated
// union narrowing on `props.entityType` doesn't survive destructuring.
const CompareCards: FC<TCompareCardsProps> = props => {
  const subtitle =
    props.mode === 'merged'
      ? 'Record was merged into primary'
      : 'Record will be merged into primary';

  const rowsForCard = (isPrimaryCard: boolean): ReactElement[] => {
    if (props.entityType === EDuplicateEntityType.PEOPLE) {
      return peopleRows(props.primary, props.secondary, isPrimaryCard);
    }
    return orgRows(props.primary, props.secondary, isPrimaryCard);
  };

  return (
    <SCompareWrapper>
      <SCompareCard $variant="primary">
        <div className="compare-card__header">
          <span>Primary record</span>
          <span className="compare-card__pill">primary</span>
        </div>
        {rowsForCard(true)}
      </SCompareCard>
      <SCompareCard $variant="secondary">
        <div className="compare-card__header">
          <span>{subtitle}</span>
          <span className="compare-card__pill">secondary</span>
        </div>
        {rowsForCard(false)}
      </SCompareCard>
    </SCompareWrapper>
  );
};

export default CompareCards;
